// Phase 2.8.2 — Control-specific telecom remediation library.
//
// One entry per telecom control (C1–C8, 32 controls total). Each entry
// provides:
//   - recommended_action:   one-paragraph, telecom-specific corrective
//                           action aligned to GSMA / 3GPP / ENISA / NIST CSF 2.0
//   - recommended_measures: bullet list of concrete measures, standards and
//                           evidence reviewers should expect to lower risk
//   - remediation_path:     numbered, implementation-oriented sequence of
//                           steps an operator can actually run
//
// These override the generic placeholder text previously produced by
// infrastructureFindingTemplates.ts. Generic B1–B4 controls continue to use
// the metadata-derived generic templates (they are not telecom-specific).
//
// All references are deliberately spelled out by their canonical document IDs
// (e.g. "GSMA FS.11", "3GPP TS 33.501", "ENISA 5G Threat Landscape") rather
// than internal pseudocodes, per project memory.

export interface ControlRemediation {
  recommended_action: string;
  recommended_measures: string;
  remediation_path: string;
}

// Small helpers to keep the bullet/numbered formatting consistent.
const measures = (refs: { standards: string[]; controls: string[]; evidence: string[] }): string =>
  [
    "Reference standards:\n- " + refs.standards.join("\n- "),
    "Control measures to lower risk:\n- " + refs.controls.join("\n- "),
    "Evidence to obtain or produce:\n- " + refs.evidence.join("\n- "),
  ].join("\n\n");

const path = (steps: string[]): string =>
  steps.map((s, i) => `${i + 1}) ${s}`).join("\n");

export const INFRASTRUCTURE_REMEDIATION_LIBRARY: Record<string, ControlRemediation> = {
  // ──────────────────────────────────────────────────────────────────────
  // C1 — Radio Access Network
  // ──────────────────────────────────────────────────────────────────────
  ran_security_hardening: {
    recommended_action:
      "Establish and enforce a documented RAN hardening baseline for every eNodeB/gNodeB and small-cell platform, covering default credentials, exposed management ports, supported protocols, patch posture and configuration drift. The baseline must be derived from 3GPP TS 33.117 (general SECAM hardening), 3GPP TS 33.216 (eNB) and 3GPP TS 33.511 (gNB), supported by GSMA FS.31 baseline controls, and verified through quarterly compliance audits.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.117 — General SECAM hardening requirements",
        "3GPP TS 33.216 — eNB security assurance",
        "3GPP TS 33.511 — gNB security assurance",
        "GSMA FS.31 — Baseline Security Controls",
        "NIST CSF 2.0 — PR.PS-01 / PR.PS-02 (configuration & patching)",
        "ENISA 5G Supplement to Threat Landscape (RAN section)",
      ],
      controls: [
        "Disable default credentials and unused management protocols (HTTP, Telnet, SNMPv1/v2c) on every RAN node",
        "Apply vendor-signed firmware only; reject unsigned or downgraded images at boot",
        "Patch SLA: critical CVEs within 14 days, high within 30 days, tracked per node",
        "Quarterly configuration drift report comparing live config against the baseline template",
        "Centralised vulnerability and inventory feed from the EMS into the SOC",
      ],
      evidence: [
        "RAN hardening baseline document with version history",
        "Latest patch-status report by site and software release",
        "Sample configuration audit (≥10% of nodes) versus baseline",
      ],
    }),
    remediation_path: path([
      "Inventory every RAN node (vendor, model, software train, location) in a single source of truth",
      "Draft the hardening baseline mapped clause-by-clause to 3GPP TS 33.117/33.216/33.511 and GSMA FS.31",
      "Pilot the baseline on a non-production cluster; capture exceptions in a register with risk owner and expiry",
      "Roll out via EMS templates; treat any drift from the template as a P2 change ticket",
      "Stand up a quarterly RAN compliance audit (sample ≥10% of nodes) with SOC visibility on findings",
      "Feed exceptions and patch-SLA misses into the operator risk register for quarterly review",
    ]),
  },

  ran_air_interface_protect: {
    recommended_action:
      "Enforce strong air-interface confidentiality and integrity algorithms across LTE and 5G bearers per 3GPP TS 33.401 and TS 33.501, disable null-cipher / null-integrity in production, and govern any residual exceptions through a documented, time-bound register reviewed quarterly to prevent downgrade and IMSI-catcher attacks.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.401 — LTE security architecture",
        "3GPP TS 33.501 — 5G security architecture",
        "GSMA FS.31 — Baseline Security Controls (air-interface section)",
        "NIST CSF 2.0 — PR.DS-02 / PR.DS-10",
        "ENISA 5G Threat Landscape (radio threats)",
      ],
      controls: [
        "Configure EEA2/EIA2 (LTE) and NEA2/NIA2 or NEA3/NIA3 (5G) as preferred algorithms; disable EEA0/NEA0 in production",
        "Enable integrity protection of user plane (UP-IP) for 5G NR where supported",
        "Block legacy A5/0 and A5/1 on any 2G/3G fallback that is still active",
        "Periodic over-the-air capture verifying advertised security capabilities at the cell edge",
        "Document and review any null-cipher exception (e.g., emergency calls) every quarter",
      ],
      evidence: [
        "Cipher and integrity policy export from EMS / NRF / AMF",
        "Null-cipher exception register (scope, justification, owner, expiry)",
        "Latest air-interface algorithm audit report",
      ],
    }),
    remediation_path: path([
      "Audit current per-cell cipher and integrity configuration across LTE and 5G",
      "Define the target algorithm matrix (EEA2/EIA2, NEA2/NIA2, NEA3/NIA3) and the exception governance process",
      "Push configuration via EMS templates; remove EEA0/NEA0 from the default selection",
      "Validate with field captures using a calibrated test UE in representative cells",
      "Stand up quarterly review of the null-cipher exception register with the security committee",
      "Add alerting in the SOC when an unexpected algorithm is negotiated at the cell",
    ]),
  },

  ran_mgmt_plane_security: {
    recommended_action:
      "Force all RAN management-plane access through authenticated NETCONF over SSH/TLS with MFA-enforced jump hosts, eliminate direct device logins, and integrate session recording and audit into the SOC, as required by 3GPP TS 33.117 and O-RAN WG11 security specifications.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.117 — General SECAM (management plane)",
        "O-RAN Alliance WG11 — Security specifications",
        "GSMA FS.31 — Baseline Security Controls",
        "NIST CSF 2.0 — PR.AA-01 / PR.AA-05 / PR.PS-01",
      ],
      controls: [
        "Disable direct CLI / web logins on RAN elements; only jump-host originated sessions permitted",
        "MFA enforced for every RAN administrator account at the jump-host layer",
        "NETCONF over SSH or TLS with certificate-based mutual authentication",
        "Centralised session recording with tamper-evident storage and ≥12-month retention",
        "Vendor and contractor accounts gated by just-in-time (JIT) approval",
      ],
      evidence: [
        "NETCONF/SSH policy and certificate inventory",
        "MFA enrolment report for RAN operations staff",
        "Sample jump-host audit log for the last 30 days",
      ],
    }),
    remediation_path: path([
      "Inventory all RAN management entry points (NMS, EMS, vendor portals, direct CLI)",
      "Deploy a hardened jump-host tier with MFA, session recording and time-bound access tokens",
      "Migrate operators and vendors off direct device access onto the jump host; block direct paths at the firewall",
      "Roll out NETCONF over SSH/TLS with mutually authenticated certificates; rotate keys per policy",
      "Pipe jump-host and NETCONF audit logs to the SIEM; create detections for off-hours or anomalous changes",
      "Run a quarterly access recertification specific to RAN operations roles",
    ]),
  },

  ran_remote_site_access: {
    recommended_action:
      "Enforce just-in-time (JIT), MFA-protected, session-recorded vendor and field-engineer access to cell sites and edge RAN nodes via a hardened jump-host tier; eliminate standing remote access and align with O-RAN WG11 and GSMA FS.31 vendor-access guidance.",
    recommended_measures: measures({
      standards: [
        "O-RAN Alliance WG11 — Vendor access security",
        "GSMA FS.31 — Baseline Security Controls (third-party access)",
        "ENISA Good Practices on Securing 5G Networks (supplier access)",
        "NIST CSF 2.0 — PR.AA-01 / PR.AA-05 / GV.SC-07",
      ],
      controls: [
        "Standing vendor accounts disabled; access granted via JIT workflow with documented business reason",
        "MFA at the jump host using phishing-resistant factors (FIDO2 / smartcard)",
        "Full session recording for every vendor session, retained ≥12 months",
        "Geo / time-window enforcement aligned to the maintenance change ticket",
        "Quarterly recertification of vendor accounts and removal of dormant identities",
      ],
      evidence: [
        "Vendor access policy and JIT workflow definition",
        "Last 90 days of JIT approval records mapped to change tickets",
        "Sample session recording index showing tamper-evident storage",
      ],
    }),
    remediation_path: path([
      "Map every active vendor and field-engineer access path into the RAN estate",
      "Stand up a JIT access platform (e.g., PAM) integrated with the change-management system",
      "Enforce MFA with phishing-resistant factors at the jump host",
      "Cut over from standing vendor accounts to JIT-only; revoke residual credentials",
      "Enable full session recording with WORM storage and retention aligned to regulator expectations",
      "Run a quarterly vendor-access recertification with the procurement and security teams",
    ]),
  },

  // ──────────────────────────────────────────────────────────────────────
  // C2 — Core Network
  // ──────────────────────────────────────────────────────────────────────
  core_5g_sba_security: {
    recommended_action:
      "Enforce mutual TLS, OAuth2 client-credentials and authenticated NRF-based NF discovery across the entire 5G Service Based Architecture, per 3GPP TS 33.501 and TS 29.510, eliminating cleartext SBI and unauthenticated NF-to-NF calls that allow NF impersonation across the core.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.501 — 5G security architecture",
        "3GPP TS 29.510 — NRF (NF discovery, OAuth2)",
        "GSMA FS.36 — 5G Core security",
        "ENISA 5G Supplement to Threat Landscape (core)",
        "NIST CSF 2.0 — PR.AA-01 / PR.DS-02",
      ],
      controls: [
        "mTLS enforced on every SBI link with certificates issued by the operator PKI",
        "OAuth2 access tokens minted by NRF; NF Service Consumers validate signature, audience and expiry",
        "Service-mesh (e.g., Istio) policy denying any unauthenticated NF-to-NF traffic by default",
        "Automated certificate lifecycle (rotation ≤90 days, revocation tested quarterly)",
        "Continuous monitoring of cleartext SBI attempts with SOC alerting",
      ],
      evidence: [
        "NRF authentication and OAuth2 configuration export",
        "SBI mTLS certificate inventory with issuance dates and expiry",
        "Service-mesh / SBI policy snapshot showing deny-by-default",
      ],
    }),
    remediation_path: path([
      "Inventory every 5G NF and the SBI links it produces and consumes",
      "Stand up an operator PKI (or onboard to an existing one) able to issue short-lived NF certificates",
      "Enable mTLS in NF-by-NF rollout, starting with the highest-risk NFs (AMF, SMF, AUSF, UDM)",
      "Activate OAuth2 token validation at every NF Service Consumer; reject calls without a valid token",
      "Deploy service-mesh policy denying any cleartext SBI; alert when violations are observed",
      "Automate certificate rotation and run a quarterly revocation drill",
    ]),
  },

  core_epc_hardening: {
    recommended_action:
      "Apply a documented EPC and IMS hardening baseline (MME, S/PGW, HSS, P/I/S-CSCF) per 3GPP TS 33.250 and GSMA FS.31, enforced through quarterly vulnerability scans, a measured patch SLA, and configuration drift detection — these legacy elements continue to underpin voice, SMS and mobile data services.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.250 — EPC security assurance",
        "3GPP TS 33.328 — IMS media plane security",
        "GSMA FS.31 — Baseline Security Controls",
        "NIST CSF 2.0 — PR.PS-01 / PR.PS-02",
      ],
      controls: [
        "Disable unused services and protocols on every EPC/IMS element",
        "Enforce strong, individually attributed admin credentials with MFA at the jump host",
        "Quarterly authenticated vulnerability scan of every node in the EPC/IMS inventory",
        "Patch SLA tied to CVSS severity, reported to security governance monthly",
        "Continuous configuration drift monitoring against the baseline template",
      ],
      evidence: [
        "EPC/IMS hardening baseline document",
        "Last quarterly vulnerability scan report with remediation status",
        "Configuration drift report covering the last 90 days",
      ],
    }),
    remediation_path: path([
      "Inventory every EPC/IMS node, software release and known exception",
      "Author a hardening baseline mapped clause-by-clause to 3GPP TS 33.250 and GSMA FS.31",
      "Apply the baseline through EMS templates; capture exceptions with risk owner and expiry",
      "Stand up authenticated quarterly scanning; integrate findings into the patch-SLA tracker",
      "Enable drift detection and treat any deviation as a P2 change ticket",
      "Brief findings monthly to security governance with risk-weighted prioritisation",
    ]),
  },

  core_sbi_api_security: {
    recommended_action:
      "Enforce full mTLS + OAuth2 + JWT signature/audience validation on every 5G SBI call, integrate misuse detection into the SOC and treat any unauthenticated or unvalidated request as a high-severity security event per 3GPP TS 33.501 and TS 29.510.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.501 — 5G security architecture",
        "3GPP TS 29.510 — NRF / OAuth2 client registration",
        "OWASP API Security Top 10 (mapped to telecom SBI)",
        "NIST CSF 2.0 — PR.AA-01 / PR.DS-02",
        "ENISA 5G Threat Landscape (API abuse)",
      ],
      controls: [
        "JWT validation at every Service Consumer: signature, issuer, audience, expiry and required scopes",
        "Per-NF Service Producer rate-limits and burst caps",
        "Replay protection (nonce / jti tracking) for high-value APIs (e.g., UDM Nudm_UECM)",
        "Centralised certificate revocation tested at least quarterly",
        "SOC detections for invalid token, expired token and scope-mismatch error spikes",
      ],
      evidence: [
        "SBI mTLS certificate inventory and rotation schedule",
        "OAuth2 client registry export from the NRF",
        "Last 30 days of JWT validation failure logs with triage outcomes",
      ],
    }),
    remediation_path: path([
      "Audit every SBI consumer to confirm JWT validation is performed (signature, audience, expiry)",
      "Close gaps in JWT validation; treat consumers that bypass validation as a P1 change",
      "Configure rate-limits and burst caps on every NF Service Producer",
      "Stand up a CRL / OCSP service for NF certificates and test revocation quarterly",
      "Onboard SBI auth events to the SIEM with detections for invalid-token and scope-mismatch spikes",
      "Run a tabletop exercise simulating an NF token-theft scenario and adjust controls",
    ]),
  },

  core_cnf_hardening: {
    recommended_action:
      "Harden every Kubernetes cluster hosting core CNFs to the CIS Kubernetes Benchmark and 3GPP TS 33.848 guidance, enforce signed-image admission, runtime monitoring and a measurable patch SLA so that a single container escape cannot compromise multiple network functions simultaneously.",
    recommended_measures: measures({
      standards: [
        "CNCF / CIS Kubernetes Benchmark (latest)",
        "3GPP TS 33.848 — Security aspects of virtualised network functions",
        "ETSI NFV-SEC 023 — Container security",
        "NIST CSF 2.0 — PR.PS-01 / PR.PS-02 / PR.PS-06",
      ],
      controls: [
        "Admission policy (OPA Gatekeeper / Kyverno) blocking privileged pods, hostPath mounts and unsigned images",
        "Image signing and verification (Sigstore cosign or equivalent) enforced cluster-wide",
        "Runtime security agent (e.g., Falco) feeding the SIEM with alerts on syscall anomalies",
        "Patch SLA for cluster components and CNFs aligned to CVSS severity",
        "Network policy denying inter-namespace traffic except via documented service mesh routes",
      ],
      evidence: [
        "Latest CIS Kubernetes Benchmark scan report",
        "Signed-image policy bundle and exception register",
        "Runtime security alert sample for the last 30 days",
      ],
    }),
    remediation_path: path([
      "Inventory every cluster, CNF and supporting image registry in the core",
      "Run the CIS Kubernetes Benchmark; track remediation by cluster owner",
      "Deploy admission controllers blocking privileged / unsigned / hostPath workloads",
      "Roll out image signing in CI/CD; reject unsigned images at admission",
      "Deploy a runtime security agent and onboard alerts to the SIEM",
      "Enforce default-deny network policy and document every approved cross-namespace flow",
    ]),
  },

  // ──────────────────────────────────────────────────────────────────────
  // C3 — Signaling Network
  // ──────────────────────────────────────────────────────────────────────
  signaling_ss7_security: {
    recommended_action:
      "Deploy and continuously tune an SS7 firewall enforcing GSMA FS.11 Category 1, 2 and 3 filtering at every SS7/SIGTRAN border, with full message inspection (MAP/CAP), per-partner whitelisting and SOC integration so that location-tracking, SMS-interception and call-redirection abuse is blocked rather than only observed.",
    recommended_measures: measures({
      standards: [
        "GSMA FS.11 — SS7 Interconnect Security (Cat 1/2/3)",
        "GSMA IR.82 — Security of the GSM/UMTS SS7 network",
        "ITU-T Q.700 series (SS7)",
        "NIST CSF 2.0 — PR.IR-01 / DE.CM-01",
        "ENISA Threat Landscape for 5G (legacy interconnect)",
      ],
      controls: [
        "Cat-1 messages from outside the home network are blocked at the edge",
        "Cat-2 messages are validated against IMSI/GT plausibility (correlation with HLR/HSS)",
        "Cat-3 messages are gated by velocity, geography and SMS-Home-Routing protections",
        "Per-roaming-partner whitelists derived from active IR.21 records",
        "SOC dashboards with alerting on filter drops, anomaly spikes and partner deviations",
      ],
      evidence: [
        "Current SS7 firewall ruleset with FS.11 mapping",
        "Last 30 days of drop statistics by category and partner",
        "Sample anomaly alert and SOC triage record",
      ],
    }),
    remediation_path: path([
      "Map every SS7/SIGTRAN ingress and egress, including any legacy TDM links",
      "Procure or upgrade to a signalling firewall supporting FS.11 Cat-1/2/3 filtering",
      "Activate Cat-1 blocking first (low operational risk), then iteratively turn on Cat-2 and Cat-3 with partner-by-partner tuning",
      "Synchronise partner whitelists with IR.21 records and rebuild them at least quarterly",
      "Integrate the firewall telemetry into the SIEM with detections for partner abuse and velocity anomalies",
      "Run an annual signalling penetration test using a known-good test partner",
    ]),
  },

  signaling_diameter_security: {
    recommended_action:
      "Deploy a Diameter Edge Agent (DEA) in path for all 4G/IMS roaming and interconnect, enforcing GSMA FS.19 Cat-1/2/3 filtering, topology hiding, rate limiting and message validation, with alerts integrated into the SOC to protect the HSS, PCRF and IMS core from cross-operator abuse.",
    recommended_measures: measures({
      standards: [
        "GSMA FS.19 — Diameter Interconnect Security",
        "3GPP TS 29.272 / 29.273 — Diameter applications",
        "GSMA IR.88 — LTE roaming",
        "NIST CSF 2.0 — PR.IR-01 / DE.CM-01",
        "ENISA Threat Landscape for 5G (Diameter)",
      ],
      controls: [
        "DEA on path for every Diameter interconnect; no direct partner peerings to internal nodes",
        "Topology hiding: internal node identities never exposed to roaming partners",
        "FS.19 Cat-1/2/3 filtering tuned per partner, with quarterly review",
        "Rate-limits and AVP whitelisting on high-risk applications (S6a, S9, Rx)",
        "SOC alerting on filter drops, partner-specific spikes and topology-leak attempts",
      ],
      evidence: [
        "DEA configuration export including FS.19 rule mapping",
        "Topology hiding configuration and verification test results",
        "Last 30 days of anomaly alerts and SOC triage outcomes",
      ],
    }),
    remediation_path: path([
      "Inventory every Diameter interconnect and the internal nodes currently exposed",
      "Insert a DEA in path for every interconnect; block direct partner-to-node flows at the IPX edge",
      "Activate topology hiding and verify externally with a trusted partner",
      "Roll out FS.19 Cat-1/2/3 filtering progressively, starting with low-risk partners",
      "Set per-partner rate-limits and AVP whitelists on S6a, S9 and Rx",
      "Onboard DEA telemetry to the SIEM and run quarterly rule reviews",
    ]),
  },

  signaling_gtp_security: {
    recommended_action:
      "Deploy a GTP-aware firewall on every S5/S8 and N4/N9 interface, enforce GSMA FS.20 baseline filtering, validate roaming-partner whitelists and alert on anomalies to prevent GTP-based subscriber impersonation, billing fraud and packet-core DoS.",
    recommended_measures: measures({
      standards: [
        "GSMA FS.20 — GPRS Tunnelling Protocol (GTP) Security",
        "3GPP TS 29.060 / 29.274 / 29.281 — GTP",
        "NIST CSF 2.0 — PR.IR-01",
        "ENISA Threat Landscape for 5G (transport / GTP)",
      ],
      controls: [
        "GTP firewall enforcing FS.20 message-type whitelisting and malformed-packet drops",
        "Per-roaming-partner source-IP and TEID whitelisting",
        "Anti-spoofing checks on GTP-U user-plane payloads (IMSI / IP plausibility)",
        "Rate-limits per partner and per node; alert when sustained burst thresholds are exceeded",
        "Monthly review of drop statistics by partner and message type",
      ],
      evidence: [
        "GTP firewall ruleset with FS.20 mapping",
        "Current roaming-partner whitelist with last review date",
        "Last 30 days of drop statistics and anomaly alerts",
      ],
    }),
    remediation_path: path([
      "Inventory every GTP-C and GTP-U interconnect (S5/S8, S2a/b, N4/N9)",
      "Deploy or upgrade to a GTP-aware firewall on each interconnect",
      "Activate FS.20 message-type whitelisting and malformed-packet drops",
      "Build per-partner source-IP / TEID whitelists from active IR.21 records",
      "Add anti-spoofing checks on the user plane and alert on IMSI/IP mismatches",
      "Run a monthly review of partner drop statistics and quarterly partner re-validation",
    ]),
  },

  roaming_cat123_filters: {
    recommended_action:
      "Apply GSMA Cat-1/2/3 filtering consistently for both SS7 (FS.11) and Diameter (FS.19) at every roaming border, with quarterly rule review and SOC alerting, eliminating partial deployments that leave one protocol or one category unprotected.",
    recommended_measures: measures({
      standards: [
        "GSMA FS.11 — SS7 Interconnect Security",
        "GSMA FS.19 — Diameter Interconnect Security",
        "GSMA IR.77 / IR.88 — Roaming control",
        "NIST CSF 2.0 — PR.IR-01 / DE.CM-01",
      ],
      controls: [
        "Cat-1/2/3 enforced on SS7 and Diameter — neither protocol partial",
        "Per-partner whitelists rebuilt quarterly from active IR.21 data",
        "SOC alerting on filter-drop spikes and per-partner anomalies",
        "Annual partner security audit covering signalling traffic patterns",
        "Documented change control for any rule relaxation, with risk owner and expiry",
      ],
      evidence: [
        "Combined SS7 + Diameter ruleset with Cat-1/2/3 mapping",
        "Quarterly partner whitelist refresh records",
        "Annual signalling audit report",
      ],
    }),
    remediation_path: path([
      "Audit current SS7 and Diameter filtering coverage; identify protocol-specific gaps",
      "Author a single roaming-filter policy covering FS.11 and FS.19 in lockstep",
      "Close the smaller-protocol gap first (commonly Diameter Cat-2/3) to reach parity",
      "Rebuild partner whitelists from IR.21 and schedule quarterly refreshes",
      "Add per-partner anomaly detection in the SIEM with SOC playbooks",
      "Commission an annual independent signalling audit",
    ]),
  },

  // ──────────────────────────────────────────────────────────────────────
  // C4 — Transport / Backhaul
  // ──────────────────────────────────────────────────────────────────────
  transport_backhaul_encrypt: {
    recommended_action:
      "Encrypt all untrusted backhaul and transport segments using IPsec (3GPP TS 33.210) or MACsec (IEEE 802.1AE) with documented key rotation and monitored tunnel health, eliminating cleartext segments where passive interception of subscriber and signalling traffic is feasible.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.210 — Network Domain Security / IP",
        "3GPP TS 33.310 — Authentication framework (PKI)",
        "IEEE 802.1AE — MACsec",
        "NIST CSF 2.0 — PR.DS-02",
        "ENISA 5G Threat Landscape (transport)",
      ],
      controls: [
        "Trust-zone model identifying which segments require IPsec vs MACsec vs none",
        "Documented key rotation (IKEv2 lifetime ≤24 h or per IEEE 802.1AE MKA) and HSM-backed key storage",
        "Tunnel health monitoring with alerting on rekey failures and prolonged down events",
        "Quarterly cipher review against current 3GPP/IETF guidance",
        "Documented exception register for any unencrypted segment, with compensating controls",
      ],
      evidence: [
        "Encryption policy mapping zone-to-mechanism (IPsec/MACsec/none)",
        "Tunnel inventory with cipher suite and last rekey timestamp",
        "Last 90 days of tunnel health dashboard data",
      ],
    }),
    remediation_path: path([
      "Map every backhaul and transport segment to a trust zone",
      "Choose IPsec (L3) or MACsec (L2) per segment based on topology and vendor support",
      "Stand up PKI (3GPP TS 33.310) for IPsec; configure MKA pre-shared or EAP-TLS for MACsec",
      "Roll out encryption segment-by-segment, starting with the highest-risk (microwave, dark fibre to third parties)",
      "Deploy tunnel health monitoring and alert on rekey failures and unscheduled down events",
      "Decommission cleartext fallback paths and update the exception register",
    ]),
  },

  transport_ipsec_3gpp: {
    recommended_action:
      "Deploy IPsec via Security Gateways (SEGs) on every S1/X2/Xn/N2/N3 interface per 3GPP TS 33.210/33.310, monitor tunnel health and certificate lifecycle, and document any exception with a time-bound mitigation plan to prevent passive interception and active manipulation of subscriber and signalling traffic.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.210 — NDS/IP",
        "3GPP TS 33.310 — Authentication framework using PKI",
        "GSMA FS.31 — Baseline Security Controls",
        "NIST CSF 2.0 — PR.DS-02 / PR.DS-10",
      ],
      controls: [
        "SEGs deployed at the boundary of every 3GPP interface in scope",
        "IPsec policies enforce IKEv2 with strong DH groups, AES-GCM ciphers and ≤24 h SA lifetimes",
        "Operator PKI issues SEG certificates with documented revocation procedures",
        "Tunnel monitoring with alerts on rekey failures, partial coverage and bypass attempts",
        "Documented exception register with risk owner, compensating control and expiry",
      ],
      evidence: [
        "IPsec policy export from each SEG",
        "SEG and certificate inventory with expiry dates",
        "Tunnel monitoring dashboard snapshot",
      ],
    }),
    remediation_path: path([
      "Map every 3GPP interface in scope (S1, X2, Xn, N2, N3) and current SEG coverage",
      "Stand up or extend the operator PKI to issue SEG certificates per 3GPP TS 33.310",
      "Deploy SEGs to fill coverage gaps, starting with the highest-exposure interfaces",
      "Activate strong cipher suites (IKEv2 + AES-GCM) and enforce ≤24 h SA lifetimes",
      "Deploy tunnel health monitoring and alert on rekey failures and bypass attempts",
      "Document any residual exception with a time-bound remediation plan",
    ]),
  },

  transport_macsec_backhaul: {
    recommended_action:
      "Roll out MACsec (IEEE 802.1AE) with MKA (IEEE 802.1X-2020) on all untrusted metro and backhaul Ethernet links, document key rotation and monitor link health to prevent trivial physical-access taps in shared vaults, manholes and roof-top runs.",
    recommended_measures: measures({
      standards: [
        "IEEE 802.1AE — MACsec",
        "IEEE 802.1X-2020 — Port-based network access control / MKA",
        "3GPP TS 33.210 (transport encryption objectives)",
        "NIST CSF 2.0 — PR.DS-02",
      ],
      controls: [
        "MACsec enabled on every untrusted L2 link (metro Ethernet, dark fibre, microwave)",
        "MKA with EAP-TLS or strong pre-shared keys; documented rotation schedule",
        "Continuous monitoring of MKA session state and unexpected re-authentications",
        "Compatible NIC / line-card inventory tracked for refresh planning",
        "Documented exception register where MACsec is technically infeasible",
      ],
      evidence: [
        "MACsec link inventory with MKA mode and last rotation",
        "MKA configuration export",
        "Link-state dashboard for the last 30 days",
      ],
    }),
    remediation_path: path([
      "Inventory every metro / backhaul L2 link and classify trust",
      "Validate MACsec support on existing NICs and line cards; plan refresh where needed",
      "Pilot MACsec on a single ring with EAP-TLS-backed MKA",
      "Roll out by ring with documented key rotation and link-state monitoring",
      "Decommission any cleartext backup paths and update the exception register",
      "Add SOC detections for unexpected MKA failures or repeated re-authentications",
    ]),
  },

  transport_segment_routing: {
    recommended_action:
      "Establish documented per-tenant and per-function segmentation across the transport network using MPLS L3VPN, Segment Routing or SRv6, audit VRF and SR policies for unintended leaks, and gate every policy change through change control to prevent lateral movement and tenant cross-talk.",
    recommended_measures: measures({
      standards: [
        "RFC 4364 — BGP/MPLS IP VPNs",
        "RFC 8402 — Segment Routing architecture",
        "RFC 8754 — SRv6 network programming",
        "NIST CSF 2.0 — PR.IR-01",
      ],
      controls: [
        "Authoritative segmentation design document mapping VRFs and SR policies to tenants/functions",
        "Quarterly automated leak audit comparing intended vs actual route imports/exports",
        "Change control on SR policies with peer review and risk owner sign-off",
        "Monitoring on route-target imports and SR policy programming events",
        "Per-tenant network telemetry surfaced to the security team for anomaly review",
      ],
      evidence: [
        "Segmentation design document with version history",
        "Latest VRF / SR leak audit report",
        "Change-control records for SR policy changes in the last 90 days",
      ],
    }),
    remediation_path: path([
      "Build the authoritative segmentation design document if missing",
      "Run an automated VRF / SR leak audit; remediate findings as P2 changes",
      "Place every SR policy change behind peer-reviewed change control",
      "Deploy monitoring on route-target imports and SR policy programming events",
      "Schedule quarterly leak audits and feed findings into the operator risk register",
      "Run an annual independent transport segmentation review",
    ]),
  },

  transport_segmentation: {
    recommended_action:
      "Maintain logical segmentation integrity across MPLS, Segment Routing and SRv6 by enforcing documented design, periodic leak audits and strict change control on VRF and SR policies, eliminating ad-hoc route leaks that violate tenant or regulatory separation.",
    recommended_measures: measures({
      standards: [
        "RFC 4364 — BGP/MPLS IP VPNs",
        "RFC 8402 — Segment Routing architecture",
        "RFC 8754 — SRv6 network programming",
        "NIST CSF 2.0 — PR.IR-01",
      ],
      controls: [
        "Authoritative segmentation design treated as a controlled document",
        "Quarterly automated leak audit comparing intended vs actual route imports/exports",
        "Change control with peer review for any VRF or SR policy change",
        "Telemetry alerting on unexpected route-target imports",
        "Annual independent segmentation audit",
      ],
      evidence: [
        "Segmentation design document (versioned)",
        "Latest leak audit report",
        "Change-control records for SR / VRF changes",
      ],
    }),
    remediation_path: path([
      "Confirm the segmentation design document exists and reflects today's reality",
      "Stand up an automated leak audit job (BGP / SR policy diff)",
      "Subject every VRF / SR change to peer-reviewed change control",
      "Deploy telemetry alerts on unexpected route-target imports",
      "Run quarterly leak audits and close findings via change control",
      "Commission an annual independent segmentation audit",
    ]),
  },

  // ──────────────────────────────────────────────────────────────────────
  // C5 — Virtualization & Edge
  // ──────────────────────────────────────────────────────────────────────
  nfv_mec_isolation: {
    recommended_action:
      "Enforce strong hypervisor and container isolation for VNFs and MEC workloads per ETSI NFV-SEC 012 and ETSI MEC 003, with CIS/STIG-hardened hosts, documented tenant boundaries and periodic isolation testing so that a compromised VNF or MEC app cannot pivot into adjacent services or signalling planes.",
    recommended_measures: measures({
      standards: [
        "ETSI NFV-SEC 012 — System architecture specification for execution of sensitive NFV components",
        "ETSI MEC 003 — Framework and reference architecture",
        "CIS / STIG benchmarks for the chosen hypervisor and OS",
        "NIST CSF 2.0 — PR.IR-01 / PR.PS-01",
      ],
      controls: [
        "Per-tenant compute / network / storage boundaries documented and enforced",
        "Hypervisor / host hardened to CIS or STIG with quarterly compliance scan",
        "Periodic isolation testing (e.g., adversary simulation pivoting between tenants)",
        "Strict separation of management and tenant networks",
        "Hardware-rooted attestation where supported (TPM / SGX / TDX)",
      ],
      evidence: [
        "NFVI security architecture document",
        "Latest CIS / STIG scan report",
        "Tenant isolation test report",
      ],
    }),
    remediation_path: path([
      "Document the NFVI / MEC tenant boundary model end-to-end",
      "Apply CIS / STIG hardening to every host; track exceptions",
      "Separate management and tenant networks at the physical / VLAN level",
      "Stand up quarterly CIS / STIG scans with remediation tracking",
      "Run an annual tenant isolation test (red-team or controlled adversary simulation)",
      "Where hardware supports it, enable TPM-based attestation for critical workloads",
    ]),
  },

  virt_hypervisor_hardening: {
    recommended_action:
      "Maintain quarterly CIS-aligned hardening of every hypervisor and host OS in the NFVI, enforce a measurable patch SLA and surface configuration drift as alertable events, so that host-layer weaknesses do not undermine the isolation guarantees of the VNFs running on top.",
    recommended_measures: measures({
      standards: [
        "CIS Benchmarks for the hypervisor and OS in use",
        "ETSI NFV-SEC 012 — NFV execution environment security",
        "NIST CSF 2.0 — PR.PS-01 / PR.PS-02",
      ],
      controls: [
        "Quarterly CIS scan of every hypervisor and host with remediation tracking",
        "Patch SLA tied to CVSS severity; reported monthly to security governance",
        "Configuration drift detection feeding the SIEM as alertable events",
        "Hardened management network with MFA at the jump host",
        "Documented golden image with cryptographic verification at build time",
      ],
      evidence: [
        "Latest CIS scan report by cluster",
        "Patch SLA tracker showing trailing 90 days",
        "Drift detection alert sample",
      ],
    }),
    remediation_path: path([
      "Adopt the relevant CIS benchmark as the authoritative baseline",
      "Build a hardened golden image and verify it cryptographically at deploy time",
      "Stand up quarterly authenticated CIS scans with per-cluster ownership",
      "Tie the patch SLA to CVSS severity and report monthly to security governance",
      "Deploy configuration drift detection and route alerts to the SIEM",
      "Run an annual independent NFVI hardening review",
    ]),
  },

  virt_k8s_rbac_admission: {
    recommended_action:
      "Implement least-privilege Kubernetes RBAC and deny-by-default admission control (OPA Gatekeeper or Kyverno) across all telecom clusters per the CIS Kubernetes Benchmark, with continuous policy-violation alerting so tenant CNFs cannot break isolation through over-broad permissions or unsafe pod specs.",
    recommended_measures: measures({
      standards: [
        "CIS Kubernetes Benchmark (latest)",
        "Kubernetes RBAC best practices",
        "ETSI NFV-SEC 023 — Container security",
        "NIST CSF 2.0 — PR.AA-05 / PR.PS-01",
      ],
      controls: [
        "No persistent cluster-admin bindings; emergency access via just-in-time workflow",
        "Per-namespace roles minimised to declared service requirements",
        "Admission policies blocking privileged, hostPath, hostNetwork and unsigned workloads",
        "Periodic RBAC audit comparing assigned vs actually used permissions",
        "Policy-violation alerts forwarded to the SIEM",
      ],
      evidence: [
        "RBAC audit report",
        "Admission policy bundle and exception register",
        "Last 30 days of policy violation alerts",
      ],
    }),
    remediation_path: path([
      "Run a cluster-wide RBAC audit; identify and remove cluster-admin and wildcard permissions",
      "Define namespace-scoped roles tied to declared service requirements",
      "Deploy OPA Gatekeeper or Kyverno with a deny-by-default admission policy bundle",
      "Add admission policies for privileged / hostPath / hostNetwork / unsigned image controls",
      "Route policy-violation events to the SIEM with SOC detections",
      "Schedule a quarterly RBAC and admission policy review",
    ]),
  },

  virt_secrets_hsm: {
    recommended_action:
      "Centralise all telecom workload secrets — keys, certificates, signalling credentials — in an HSM-backed vault (FIPS 140-3) with documented rotation, scoped access and full audit logging, eliminating secrets stored in code, config files or environment variables.",
    recommended_measures: measures({
      standards: [
        "FIPS 140-3 — Cryptographic module validation",
        "ISO/IEC 27001 A.8.24 — Use of cryptography",
        "GSMA FS.31 (key management baseline)",
        "NIST CSF 2.0 — PR.DS-01 / PR.DS-02",
      ],
      controls: [
        "All workload secrets sourced from the vault at runtime; no static secrets in CI/CD or images",
        "HSM-backed root of trust for high-value keys (SBI, IPsec, MACsec, OAuth signing)",
        "Documented rotation schedule per secret class with automated enforcement",
        "Scoped access tied to workload identity (e.g., SPIFFE) rather than long-lived tokens",
        "Full audit logging of secret reads and policy changes",
      ],
      evidence: [
        "Vault / HSM architecture document",
        "Secret rotation schedule with last rotation timestamps",
        "Sample audit log entries for privileged secret access",
      ],
    }),
    remediation_path: path([
      "Inventory current secrets sprawl across code, config, CI/CD and runtime",
      "Deploy an HSM-backed vault and onboard the highest-risk secrets first (SBI, IPsec, OAuth signing)",
      "Wire workloads to retrieve secrets at runtime using workload identity",
      "Automate rotation per secret class and remove static copies from CI/CD",
      "Enable full audit logging and forward to the SIEM",
      "Run a quarterly secrets-sprawl scan to confirm no regression",
    ]),
  },

  // ──────────────────────────────────────────────────────────────────────
  // C6 — OSS / BSS
  // ──────────────────────────────────────────────────────────────────────
  oss_bss_access_control: {
    recommended_action:
      "Place every privileged OSS/BSS account behind a Privileged Access Management (PAM) platform with MFA, session recording, quarterly access reviews and automated Joiner/Mover/Leaver workflows, removing shared admin accounts and standing entitlements that enable fraud and large-scale subscriber data exposure.",
    recommended_measures: measures({
      standards: [
        "TMF 632 — Party Management",
        "ISO/IEC 27001 A.5.15–A.5.18 (access management)",
        "NIST CSF 2.0 — PR.AA-01 / PR.AA-05",
        "GSMA FS.31 — Baseline Security Controls",
      ],
      controls: [
        "All privileged OSS/BSS access via PAM with MFA and session recording",
        "Automated JML workflow tied to the HR / identity source of truth",
        "Quarterly access recertification with named approvers per system",
        "Just-in-time elevation for break-glass scenarios with after-the-fact review",
        "Detections on PAM bypass attempts and unusual privileged activity",
      ],
      evidence: [
        "PAM configuration and onboarded-systems list",
        "Last quarterly access review records",
        "Automated JML workflow definition with last-90-days execution log",
      ],
    }),
    remediation_path: path([
      "Inventory every privileged OSS/BSS account and current access path",
      "Deploy PAM and onboard the highest-risk systems first (billing, provisioning, subscriber data)",
      "Enforce MFA and session recording at the PAM tier; disable direct admin access",
      "Wire the JML workflow to the HR / identity source of truth",
      "Run a quarterly access recertification with named approvers",
      "Add SOC detections for PAM bypass attempts and anomalous privileged activity",
    ]),
  },

  ossbss_api_security: {
    recommended_action:
      "Front every TMF Open API with an API gateway enforcing OAuth2 client-credentials, per-client rate-limits, schema validation and anomaly alerting per TMF 630, eliminating static API keys and unmonitored endpoints that are a leading telecom fraud vector.",
    recommended_measures: measures({
      standards: [
        "TMF 630 — API Design Guidelines",
        "TMF 632 — Party Management",
        "OWASP API Security Top 10",
        "NIST CSF 2.0 — PR.AA-01 / PR.DS-02",
      ],
      controls: [
        "OAuth2 client-credentials with short-lived tokens for every TMF API client",
        "Per-client rate-limits and burst caps tied to commercial agreement",
        "Schema validation on every request and response",
        "Anomaly detection on error rates, latency and payload-size spikes",
        "Quarterly rotation of OAuth2 client secrets and review of dormant clients",
      ],
      evidence: [
        "API gateway configuration and onboarded API inventory",
        "OAuth2 client registry with last-rotation timestamps",
        "Last 30 days of anomaly alerts with triage outcomes",
      ],
    }),
    remediation_path: path([
      "Inventory every TMF API and its current authentication mechanism",
      "Deploy or extend an API gateway in front of every TMF API",
      "Migrate clients from static keys to OAuth2 client-credentials with short-lived tokens",
      "Configure per-client rate-limits and schema validation",
      "Onboard anomaly events to the SIEM and define SOC detections",
      "Run a quarterly review of dormant clients and rotate active secrets",
    ]),
  },

  ossbss_provisioning_integrity: {
    recommended_action:
      "Apply layered anti-fraud controls — multi-factor proof, cooling-off periods, real-time anomaly detection and customer notification — to every SIM-swap, port-in/out, eSIM activation and high-risk account change per GSMA FS.28 and any local regulator SIM-swap guidance, directly reducing account-takeover and 2FA-bypass fraud.",
    recommended_measures: measures({
      standards: [
        "GSMA FS.28 — Combating SIM-swap fraud",
        "GSMA TS.43 — eSIM remote provisioning",
        "Local regulator SIM-swap rules (where applicable)",
        "NIST CSF 2.0 — PR.AA-01 / DE.CM-09",
      ],
      controls: [
        "Multi-factor proof (knowledge + possession) for SIM-swap and port-in/out",
        "Cooling-off period (typically 24–72 h) before high-value services resume on a new SIM",
        "Real-time anomaly detection (geo, velocity, device) feeding fraud operations",
        "Customer notification on every high-risk change with self-service block option",
        "Quarterly fraud-rule tuning informed by case data",
      ],
      evidence: [
        "Anti-fraud rule set and decision flow",
        "Fraud monitoring dashboard with last 30 days of high-risk events",
        "Customer notification template and delivery statistics",
      ],
    }),
    remediation_path: path([
      "Map every high-risk subscriber change flow (SIM swap, port-in/out, eSIM, account change)",
      "Apply multi-factor proof at the start of each flow",
      "Introduce a cooling-off period before high-value services resume on a new SIM",
      "Deploy real-time anomaly detection and route alerts to fraud operations",
      "Send customer notifications with self-service block options",
      "Tune fraud rules quarterly based on case data and emerging patterns",
    ]),
  },

  ossbss_audit_logging: {
    recommended_action:
      "Ensure comprehensive, tamper-evident audit logging across NMS, EMS, OSS and BSS with regulator-aligned retention (typically ≥12 months), centralised in a SIEM with documented coverage so incident investigation and regulatory reporting do not fail at the moment they are needed.",
    recommended_measures: measures({
      standards: [
        "ISO/IEC 27001 A.8.15 — Logging",
        "Local telecom retention rules (regulator-specific)",
        "NIST CSF 2.0 — DE.CM-01 / DE.CM-09 / PR.PS-04",
      ],
      controls: [
        "SIEM coverage matrix listing every NMS/EMS/OSS/BSS system and the events captured",
        "Tamper-evident (WORM or append-only) storage for audit logs",
        "Retention aligned to regulator (typically ≥12 months online, longer in cold storage)",
        "Time synchronisation across all sources via authenticated NTP",
        "Sample queries documented for the most common privileged-action investigations",
      ],
      evidence: [
        "SIEM coverage matrix",
        "Log retention policy with regulator citation",
        "Sample privileged-action query and result set",
      ],
    }),
    remediation_path: path([
      "Build or refresh the SIEM coverage matrix; identify systems missing event sources",
      "Onboard missing sources, starting with privileged-action and authentication events",
      "Move log storage to a WORM / append-only tier with regulator-aligned retention",
      "Enforce authenticated NTP across all sources",
      "Document standard investigative queries (privileged actions, account changes)",
      "Run an annual logging assurance review with internal audit",
    ]),
  },

  // ──────────────────────────────────────────────────────────────────────
  // C7 — Subscriber / Roaming / IoT
  // ──────────────────────────────────────────────────────────────────────
  roaming_ipx_security: {
    recommended_action:
      "Sign a security annex with every roaming partner aligned to GSMA IR.77 and FS.21, layer signalling firewalls and ACLs at every IPX edge, and continuously monitor partner traffic so that roaming/IPX interconnects are not the soft underbelly of an otherwise hardened network.",
    recommended_measures: measures({
      standards: [
        "GSMA IR.77 — IPX Network end-to-end",
        "GSMA FS.21 — Interconnect signalling security",
        "GSMA FS.31 — Baseline Security Controls",
        "NIST CSF 2.0 — PR.IR-01 / GV.SC-07",
      ],
      controls: [
        "Signed security annex with every roaming partner covering signalling, fraud and incident-response obligations",
        "Layered filtering at the IPX edge: ACLs, SS7/Diameter/GTP firewalls, anti-spoofing",
        "Per-partner whitelists rebuilt quarterly from IR.21",
        "Continuous monitoring with per-partner anomaly dashboards",
        "Annual partner security review with documented evidence requests",
      ],
      evidence: [
        "Roaming security annex template and signed copies",
        "IPX edge ACLs and signalling firewall ruleset",
        "Last 30 days of per-partner anomaly dashboard data",
      ],
    }),
    remediation_path: path([
      "Update the roaming security annex template to current GSMA IR.77 / FS.21 requirements",
      "Roll out the new annex to every roaming partner during the next renewal cycle",
      "Layer ACLs, signalling firewalls and anti-spoofing at every IPX edge",
      "Build per-partner anomaly dashboards in the SIEM",
      "Run an annual security review with each partner",
      "Feed findings into the partner risk register with documented remediation",
    ]),
  },

  subscriber_data_protection: {
    recommended_action:
      "Protect HSS/UDM subscriber profiles and authentication material with SUCI concealment, HSM-backed key storage, least-privilege OSS access and monitored exports per 3GPP TS 33.501 and GSMA FS.31, preventing mass impersonation and interception arising from credential or identifier exposure.",
    recommended_measures: measures({
      standards: [
        "3GPP TS 33.501 — SUCI / SUPI concealment",
        "GSMA FS.31 — Baseline Security Controls (subscriber data)",
        "ISO/IEC 27001 A.8.24 — Use of cryptography",
        "NIST CSF 2.0 — PR.DS-01 / PR.DS-02",
      ],
      controls: [
        "SUCI concealment enforced; plaintext IMSI/SUPI eliminated wherever supported",
        "Authentication keys stored in HSM-backed key vault with FIPS 140-3 modules",
        "Least-privilege OSS access to HSS/UDM with PAM, MFA and session recording",
        "Monitoring and alerting on bulk reads or exports of subscriber data",
        "Documented data-export process with security committee sign-off",
      ],
      evidence: [
        "HSS/UDM access control configuration",
        "SUCI / SUPI configuration evidence",
        "Last 90 days of bulk-export alerts and triage records",
      ],
    }),
    remediation_path: path([
      "Confirm SUCI configuration on every UE-supporting NF and remediate plaintext exposure",
      "Migrate authentication keys into the HSM-backed vault",
      "Place every privileged HSS/UDM account behind PAM with MFA and session recording",
      "Deploy detections for bulk reads and exports of subscriber data",
      "Document the data-export process with mandatory security sign-off",
      "Run an annual subscriber-data assurance review",
    ]),
  },

  iot_m2m_gateway_security: {
    recommended_action:
      "Segregate IoT/M2M traffic onto dedicated APNs or slices with per-device rate-limits, behavioural anomaly detection and tight egress filtering per GSMA CLP.13 and ETSI EN 303 645, preventing compromised devices from being a launchpad against the core network.",
    recommended_measures: measures({
      standards: [
        "GSMA CLP.13 — IoT Security Guidelines",
        "ETSI EN 303 645 — Cyber Security for Consumer IoT",
        "GSMA FS.31 — Baseline Security Controls",
        "NIST CSF 2.0 — PR.IR-01 / DE.CM-01",
      ],
      controls: [
        "Dedicated APNs or 5G slices per IoT customer / use-case class",
        "Per-device rate-limits and session caps",
        "Egress filtering whitelisting only documented application endpoints",
        "Behavioural anomaly detection feeding the SOC (volume, geography, time-of-day)",
        "Device onboarding integrated with eSIM/iSIM provisioning controls",
      ],
      evidence: [
        "IoT segmentation diagram and APN / slice register",
        "M2M APN security policy",
        "Anomaly detection rules and last-30-days alert sample",
      ],
    }),
    remediation_path: path([
      "Inventory all IoT/M2M customers and the APNs / slices currently in use",
      "Migrate mixed traffic onto dedicated APNs or slices per customer / use-case class",
      "Configure per-device rate-limits and session caps",
      "Apply egress whitelisting based on documented application endpoints",
      "Deploy behavioural anomaly detection feeding the SOC",
      "Tighten device onboarding via eSIM/iSIM provisioning controls",
    ]),
  },

  // ──────────────────────────────────────────────────────────────────────
  // C8 — Service Continuity
  // ──────────────────────────────────────────────────────────────────────
  service_continuity_geo_red: {
    recommended_action:
      "Deploy critical network functions and data stores across at least two geographically separated sites with tested automated failover, aligned to ISO 22301 and ITU-T E.800, so that a single-site failure cannot collapse national services or regulated emergency calling.",
    recommended_measures: measures({
      standards: [
        "ISO 22301 — Business continuity management",
        "ITU-T E.800 — Quality of service",
        "GSMA FS.31 — Baseline Security Controls (resilience)",
        "NIST CSF 2.0 — RC.RP-01 / PR.IR-04",
      ],
      controls: [
        "Geo-redundant deployment for every critical NF (AMF, SMF, UDM, HSS, IMS CSCFs)",
        "Tested automated failover with documented RTO/RPO per service",
        "Data replication strategy aligned with regulatory data-residency rules",
        "Annual live failover exercise (not tabletop only)",
        "Independent observability of replication health and failover readiness",
      ],
      evidence: [
        "Geo-redundancy architecture document",
        "Last live failover exercise report",
        "Replication health dashboard snapshot",
      ],
    }),
    remediation_path: path([
      "Identify every critical NF and its current redundancy posture",
      "Design geo-redundant deployment respecting data-residency rules",
      "Deploy across ≥2 sites, starting with the highest-criticality NFs",
      "Configure automated failover and validate RTO/RPO targets",
      "Stand up replication health observability with SOC visibility",
      "Run an annual live failover exercise with after-action remediation",
    ]),
  },

  continuity_rto_rpo_documented: {
    recommended_action:
      "Document and validate per-service Recovery Time and Recovery Point Objectives for every critical telecom service, aligned to ISO 22301 and ITU-T E.800, and review them annually against actual test results so that recovery plans can be verified against regulator and customer commitments.",
    recommended_measures: measures({
      standards: [
        "ISO 22301 — Business continuity management",
        "ITU-T E.800 — Quality of service",
        "NIST CSF 2.0 — ID.IM-04 / RC.RP-01 / RC.RP-02",
      ],
      controls: [
        "Service criticality matrix covering every customer-facing and regulated service",
        "Per-service RTO/RPO documented and approved by service owner",
        "Annual review comparing documented RTO/RPO to actual test and incident results",
        "Mapping of RTO/RPO to dependent NFs and supporting infrastructure",
        "Communication of RTO/RPO to relevant customers and regulators where contracted",
      ],
      evidence: [
        "Service criticality matrix",
        "RTO/RPO register with service-owner approvals",
        "Annual review record comparing documented vs actual",
      ],
    }),
    remediation_path: path([
      "Build the service criticality matrix if missing",
      "Set per-service RTO/RPO with the service owner and security committee",
      "Map each RTO/RPO to dependent NFs and supporting infrastructure",
      "Compare documented targets against the last 12 months of test and incident data",
      "Adjust targets or recovery design where actuals do not meet documented targets",
      "Run an annual RTO/RPO review with service owners and regulator-facing stakeholders",
    ]),
  },

  continuity_dr_exercises: {
    recommended_action:
      "Run at least an annual live disaster-recovery / failover exercise for critical telecom services per ISO 22301 and NIST SP 800-84, with documented after-action reporting and remediation tracking, so that recovery plans are validated under realistic conditions rather than only on paper.",
    recommended_measures: measures({
      standards: [
        "ISO 22301 — Business continuity management",
        "NIST SP 800-84 — Test, training and exercise programs",
        "NIST CSF 2.0 — RC.RP-01 / ID.IM-02",
      ],
      controls: [
        "Annual or more frequent live exercise (failover, not just tabletop)",
        "After-action report capturing actual RTO/RPO observed and gaps identified",
        "Remediation tracker with named owners and target dates",
        "Exercise scenarios rotated across services to avoid blind spots",
        "Regulator and customer notification protocols rehearsed alongside technical recovery",
      ],
      evidence: [
        "DR exercise schedule for the last 24 months",
        "Most recent after-action report",
        "Remediation tracker with closure evidence",
      ],
    }),
    remediation_path: path([
      "Build a rolling 24-month DR exercise schedule covering every critical service",
      "Define exercise scenarios with realistic injects and stakeholder roles",
      "Run the next exercise as a live failover (not tabletop)",
      "Capture an after-action report quantifying actual RTO/RPO",
      "Load remediation items into a tracker with named owners and target dates",
      "Brief outcomes to the security committee and feed lessons into the recovery design",
    ]),
  },

  continuity_nf_redundancy: {
    recommended_action:
      "Maintain documented N+1 (or better) redundancy for every critical network function and supporting infrastructure per ITU-T E.800 and TIA-942, with an actively maintained single-points-of-failure register, so that routine faults, maintenance windows and attacks do not become customer-visible outages.",
    recommended_measures: measures({
      standards: [
        "ITU-T E.800 — Quality of service",
        "TIA-942 — Telecommunications infrastructure standard for data centres",
        "NIST CSF 2.0 — PR.IR-04 / RC.RP-01",
      ],
      controls: [
        "Documented N+1 redundancy posture per critical NF",
        "Actively maintained SPOF register with target close-out dates",
        "Capacity plan integrated with the redundancy posture",
        "Validation of redundancy through scheduled component-failure tests",
        "Quarterly review of redundancy and capacity with operations governance",
      ],
      evidence: [
        "Redundancy matrix per critical NF",
        "Current SPOF register with status",
        "Capacity plan reflecting redundancy headroom",
      ],
    }),
    remediation_path: path([
      "Build a redundancy matrix listing every critical NF and current posture",
      "Open a SPOF register; load all known single-instance components with target dates",
      "Tie capacity planning to redundancy posture so growth does not erode headroom",
      "Schedule component-failure tests to validate redundancy in practice",
      "Close out SPOFs in priority order with documented evidence",
      "Brief redundancy and SPOF status quarterly to operations governance",
    ]),
  },
};

export function getInfrastructureRemediation(controlKey: string): ControlRemediation | undefined {
  return INFRASTRUCTURE_REMEDIATION_LIBRARY[controlKey];
}

// Canonical list of telecom control keys (C1–C8) covered by this library.
// Used by the parity test to guarantee 100% coverage of telecom controls.
export const TELECOM_REMEDIATION_KEYS: readonly string[] = Object.freeze([
  // C1
  "ran_security_hardening", "ran_air_interface_protect",
  "ran_mgmt_plane_security", "ran_remote_site_access",
  // C2
  "core_5g_sba_security", "core_epc_hardening",
  "core_sbi_api_security", "core_cnf_hardening",
  // C3
  "signaling_ss7_security", "signaling_diameter_security",
  "signaling_gtp_security", "roaming_cat123_filters",
  // C4
  "transport_backhaul_encrypt", "transport_ipsec_3gpp",
  "transport_macsec_backhaul", "transport_segment_routing", "transport_segmentation",
  // C5
  "nfv_mec_isolation", "virt_hypervisor_hardening",
  "virt_k8s_rbac_admission", "virt_secrets_hsm",
  // C6
  "oss_bss_access_control", "ossbss_api_security",
  "ossbss_provisioning_integrity", "ossbss_audit_logging",
  // C7
  "roaming_ipx_security", "subscriber_data_protection", "iot_m2m_gateway_security",
  // C8
  "service_continuity_geo_red", "continuity_rto_rpo_documented",
  "continuity_dr_exercises", "continuity_nf_redundancy",
]);
